import { DatePicker } from '@liza/ui/components/atoms/DatePicker';
import { TimeField } from '@liza/ui/components/atoms/TimeField';
import { cn } from '@liza/ui/utils/cn';
import { useState } from 'react';

export interface DateTimePickerProps {
  /** `yyyy-MM-dd'T'HH:mm` value, or `''` when empty. */
  value: string;
  /** Called with the new combined `yyyy-MM-dd'T'HH:mm` value, or `''` when cleared. */
  onChange: (value: string) => void;
  id?: string;
  onBlur?: () => void;
  required?: boolean;
  disabled?: boolean;
  /** Accessible label for the date field. The time field derives its label from it. */
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  className?: string;
  /** Applied to the date input; the time field gets `${data-testid}-time`. */
  'data-testid'?: string;
}

type DateTimeParts = { date: string; time: string };

function splitValue(value: string): DateTimeParts {
  const [date = '', time = ''] = value ? value.split('T') : [];
  return { date, time };
}

/**
 * Date-and-time picker composing {@link DatePicker} (calendar popover) with a
 * {@link TimeField}. Replaces the native `<input type="datetime-local">`. Splits
 * and recombines the `yyyy-MM-dd'T'HH:mm` value so each half edits independently
 * while preserving the other.
 */
export function DateTimePicker({
  value,
  onChange,
  id,
  onBlur,
  required,
  disabled,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  className,
  'data-testid': dataTestId,
}: DateTimePickerProps): React.ReactNode {
  // A native time input reports `''` until every segment is filled — the `0` on the way to `02:00`
  // arrives here as an empty time — and half of a datetime isn't expressible in the value contract.
  // Holding both halves locally lets the inputs keep what was typed while `onChange` only ever
  // reports a complete value or `''`, never `yyyy-MM-ddT`, which parses to Invalid Date.
  const [parts, setParts] = useState(() => splitValue(value));
  // `value` wins whenever it differs from what was last reported from here, which is what lets an
  // externally set datetime ("Find available times" picking a slot) replace both halves. A consumer
  // clearing to `''` while a half is unreported — itself `''` — is indistinguishable from that
  // report, so a field that must reset to empty in place has to remount.
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setParts(splitValue(value));
  }

  const commit = (next: DateTimeParts): void => {
    const combined = next.date && next.time ? `${next.date}T${next.time}` : '';
    setParts(next);
    setLastValue(combined);
    onChange(combined);
  };

  const handleDateChange = (nextDate: string): void => {
    // A date picked before any time is a complete selection; midnight is its time.
    commit({ date: nextDate, time: nextDate && !parts.time ? '00:00' : parts.time });
  };

  const handleTimeChange = (nextTime: string): void => {
    commit({ date: parts.date, time: nextTime });
  };

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <DatePicker
        id={id}
        value={parts.date}
        onChange={handleDateChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        data-testid={dataTestId}
        className="flex-1"
      />
      <TimeField
        value={parts.time}
        onChange={handleTimeChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel ? `${ariaLabel} time` : 'Time'}
        aria-invalid={ariaInvalid}
        data-testid={dataTestId ? `${dataTestId}-time` : undefined}
        className="w-32"
      />
    </div>
  );
}
